import {
  Layout, Maximize2, Minimize2, Upload, CreditCard, Images, Image,
  Clock, Lock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { getToken } from '../../utils/authToken';
import PremiumCrownBadge from '../PremiumCrownBadge';

/**
 * WorkspaceTopBar — extraído de Dashboard.jsx en P3.1 sesión 2/4 (feb-2026).
 *
 * Contiene el header del Workspace: título, botones de expandir, publicar,
 * toggle suscripción, badge Premium del creador, botón Imágenes y botón
 * Historial. JSX y clases Tailwind IDÉNTICOS al original.
 *
 * Reglas del cuadro negro:
 * - NO cambia rutas, endpoints, handlers, textos ni clases.
 * - Toda la lógica vive en Dashboard.jsx, este componente solo presenta.
 */
const WorkspaceTopBar = ({
  // toggle expand / collapse del workspace
  isWorkspaceExpanded,
  onExpandWorkspace,
  // publicar
  jsxPreviewCode,
  lastExecutableCodeBlock,
  setShowPublishModal,
  // suscripción widget
  showSubscriptionWidget,
  setShowSubscriptionWidget,
  // badges
  isCreator,
  data,
  // imágenes
  latestWorkspaceImage,
  showImageEditor,
  workspaceImages,
  workspaceVariants,
  setShowGalleryFromHeader,
  // historial
  isPremium,
  trashBadgeCount,
}) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="flex-shrink-0 border-b border-slate-700/50 bg-slate-800/50">
      <div className="flex items-center justify-between px-2 sm:px-4 pt-[19px] sm:pt-[23px] pb-[1px] sm:pb-[5px]">
        {/* Título: Área de Trabajo */}
        <div className="flex items-center gap-1 sm:gap-2">
          <span className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-white">
            <Layout className="w-4 h-4 text-indigo-500" />
            {t('workspace.title')}
          </span>

          {/* Botón expandir */}
          {onExpandWorkspace && (
            <button
              onClick={onExpandWorkspace}
              className="p-1.5 rounded-lg transition-all text-slate-400 hover:text-white hover:bg-slate-700/50"
              title={isWorkspaceExpanded ? t('workspace.collapse') : t('workspace.expand')}
              data-testid="expand-workspace-btn"
            >
              {isWorkspaceExpanded ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Botón publicar en Apps Públicas */}
          <button
            onClick={() => {
              const token = getToken();
              if (!token || token === 'guest') {
                toast.error(language === 'es' ? 'Inicia sesión para publicar' : 'Login to publish');
                return;
              }
              const hasCode =
                (jsxPreviewCode && jsxPreviewCode.length > 50) || lastExecutableCodeBlock?.code;
              if (!hasCode) {
                toast.error(
                  language === 'es'
                    ? 'No hay código para publicar. Crea una app primero.'
                    : 'No code to publish. Create an app first.',
                );
                return;
              }
              setShowPublishModal(true);
            }}
            className="p-1.5 rounded-lg transition-all text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/20"
            title={language === 'es' ? 'Publicar en Apps Públicas' : 'Publish to Public Apps'}
            data-testid="publish-to-public-apps-btn"
          >
            <Upload className="w-4 h-4" />
          </button>

          {/* Botón toggle suscripción - siempre visible para cambiar de plan */}
          <button
            onClick={() => setShowSubscriptionWidget((prev) => !prev)}
            className={`p-1.5 rounded-lg transition-all ${
              showSubscriptionWidget
                ? 'text-cyan-400 bg-cyan-500/20 hover:bg-cyan-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
            }`}
            title={
              showSubscriptionWidget
                ? language === 'es'
                  ? 'Ocultar panel de suscripcion'
                  : 'Hide subscription panel'
                : language === 'es'
                  ? 'Mostrar panel de suscripcion'
                  : 'Show subscription panel'
            }
            data-testid="toggle-subscription-widget-btn"
          >
            <CreditCard className="w-4 h-4" />
          </button>

          {/* Corona rosada Premium — visible para Creator y Plan Premium
              (feb-2026): por orden del Owner, la representación oficial
              de Premium es esta corona rosa, no la amarilla del header
              (que se eliminó por generar confusión a los usuarios). */}
          {(isCreator || isPremium) && <PremiumCrownBadge language={language} subscriptionData={data} />}
        </div>

        {/* Info de contenido y botón Imágenes */}
        <div className="flex items-center gap-3">
          {latestWorkspaceImage && showImageEditor && (
            <span className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Image className="w-3.5 h-3.5" />
              {workspaceImages.length + workspaceVariants.length} {t('workspace.items')}
            </span>
          )}

          {/* Botón Imágenes - Siempre visible en el header */}
          {(!latestWorkspaceImage || !showImageEditor) && (
            <button
              onClick={() => {
                setShowGalleryFromHeader(true);
              }}
              className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-500 transition-colors"
              data-testid="header-gallery-btn"
            >
              <Images className="w-3.5 h-3.5" />
              {t('workspace.images')}
            </button>
          )}

          {/* Botón Historial Avanzado — visible SIEMPRE.
             - Premium o Free con items en Papelera → click navega a /historial.
             - Free sin acceso → click navega a /planes (upsell). En hover se
               muestra tooltip "Solo Premium" (mismo patrón que microfono /
               leer textos).
             Siempre visible para que la presencia del feature sea evidente
             y el Free pueda descubrirlo aunque no tenga acceso. */}
          {(() => {
            const canAccessHistory = isPremium || trashBadgeCount > 0;
            const tooltip = canAccessHistory
              ? trashBadgeCount > 0
                ? language === 'es'
                  ? `${trashBadgeCount} en Papelera`
                  : `${trashBadgeCount} in Trash`
                : language === 'es'
                  ? 'Historial Avanzado'
                  : 'Advanced History'
              : language === 'es'
                ? 'Solo Premium'
                : 'Premium only';
            return (
              <button
                onClick={() => {
                  if (canAccessHistory) {
                    navigate('/historial');
                  } else {
                    navigate('/planes');
                  }
                }}
                className={`relative flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  canAccessHistory
                    ? 'bg-amber-600 text-white hover:bg-amber-500'
                    : 'bg-amber-600/40 text-white/70 hover:bg-amber-600/60 cursor-help'
                }`}
                data-testid="header-historial-btn"
                title={tooltip}
                aria-label={tooltip}
              >
                <Clock className="w-3.5 h-3.5" />
                {language === 'es' ? 'Historial' : 'History'}
                {!canAccessHistory && <Lock className="w-3 h-3 opacity-80" />}
                {trashBadgeCount > 0 && canAccessHistory && (
                  <span
                    data-testid="trash-badge"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold leading-none ring-2 ring-zinc-900 bg-red-500 text-white animate-pulse"
                  >
                    {trashBadgeCount > 99 ? '99+' : trashBadgeCount}
                  </span>
                )}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceTopBar;
